import React from 'react';
import { Loader2 } from 'lucide-react';
import { Modal } from '../../ui/Modal';
import { Field, Input, Checkbox } from '../../ui/Field';
import { pillPrimary, pillSecondary } from '../../ui/pills';
import { moduleApi } from '../../../lib/api';

/** Garde-fous des dates de clôture — les mêmes que sur la page de configuration du menu : une relance
 *  postérieure à la clôture n'arriverait jamais, et une clôture après l'événement n'a aucun sens (la
 *  cuisine a besoin des choix AVANT le jour J). */
export const erreurDatesCloture = (prewarn: string, close: string, eventDay: string): string => {
    if (prewarn && close && prewarn > close) return "L'email d'avertissement doit partir au plus tard le dernier jour pour répondre.";
    if (eventDay && close && close > eventDay) return "Le dernier jour pour répondre doit précéder l'événement.";
    if (prewarn && !close) return "Précisez d'abord le dernier jour pour répondre : l'email annonce cette date limite.";
    return '';
};

export interface ClotureReglage { closeDate: string; prewarnDate: string; closeNotify: boolean }

/**
 * « Changer la date » du bandeau de clôture (maquette 18b) : la date est réglée ICI, et annoncée dans
 * chaque courriel envoyé aux convives. Mêmes trois réglages et même route que la carte « Clôture
 * programmée » de la page de configuration — celle-ci reste en place, les deux écrivent au même endroit.
 *
 * Des DATES seules, sans heure — « les réponses ferment le 28 août » se comprend mieux qu'un horaire à la
 * minute (décision d'Emeni). Le convive dispose de la journée entière du dernier jour.
 */
export function ClotureDateModal({ eventId, eventDay, initial, prewarningSentAt, onSaved, onClose }: {
    eventId: string;
    eventDay: string;
    initial: ClotureReglage;
    prewarningSentAt?: string | null;
    onSaved: () => void;
    onClose: () => void;
}) {
    const [closeDate, setCloseDate] = React.useState(initial.closeDate);
    const [prewarnDate, setPrewarnDate] = React.useState(initial.prewarnDate);
    const [closeNotify, setCloseNotify] = React.useState(initial.closeNotify);
    const [saving, setSaving] = React.useState(false);
    const [erreur, setErreur] = React.useState('');
    const garde = erreurDatesCloture(prewarnDate, closeDate, eventDay);
    const inchange = closeDate === initial.closeDate && prewarnDate === initial.prewarnDate && closeNotify === initial.closeNotify;

    const enregistrer = async () => {
        if (garde) return;
        setSaving(true);
        setErreur('');
        try {
            await moduleApi.updateEvenementielCollecte(eventId, {
                responses_close_date: closeDate || '',
                prewarning_date: prewarnDate || '',
                close_notify_enabled: closeNotify,
            });
            onSaved();
        } catch (e: any) {
            setErreur(e?.message || "Impossible d'enregistrer les dates de clôture.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal open onClose={onClose} size="md" title="Clôture des réponses"
            subtitle="Facultatif : sans date, vous clôturez à la main quand vous le souhaitez."
            footer={
                <>
                    <button type="button" onClick={onClose} className={pillSecondary()} disabled={saving}>Annuler</button>
                    <button type="button" onClick={enregistrer} className={pillPrimary()} disabled={saving || !!garde || inchange}>
                        {saving ? <Loader2 size={13} className="animate-spin" /> : null} Enregistrer la date
                    </button>
                </>
            }>
            <div className="space-y-3.5">
                <Field label="Dernier jour pour répondre" help="Les convives ont toute la journée de cette date ; les réponses ferment ensuite automatiquement, et la date est annoncée dans chaque courriel.">
                    {({ id }) => <Input id={id} type="date" value={closeDate} onChange={(e) => setCloseDate(e.target.value)} max={eventDay || undefined} />}
                </Field>
                <Field label="Prévenir les non-répondants le"
                    help={prewarningSentAt && prewarnDate === initial.prewarnDate
                        ? "L'email d'avertissement a déjà été envoyé. Changer la date en fera partir un nouveau."
                        : "Un email part ce matin-là à ceux qui n'ont pas encore répondu, leur rappelant la date limite."}
                    error={garde || undefined}>
                    {({ id, invalid }) => <Input id={id} type="date" value={prewarnDate} onChange={(e) => setPrewarnDate(e.target.value)} max={closeDate || undefined} invalid={invalid} />}
                </Field>
                {/* Sans cette case, la fermeture automatique se faisait en silence et le convive découvrait seul
                    qu'il avait laissé passer le délai. Cochée par défaut. */}
                <Checkbox checked={closeNotify} onChange={(e) => setCloseNotify(e.target.checked)}
                    label="Prévenir les non-répondants de la fermeture"
                    sub="À la clôture, ceux qui n'ont pas répondu reçoivent le courriel « Clôture » — le même que le bouton « Clôturer la collecte »." />
                {erreur && <p className="text-[12px] text-state-danger font-semibold">{erreur}</p>}
            </div>
        </Modal>
    );
}
